import { useMemo } from 'react'
import { useInvestigationsByPatientHashQuery, useInvestigationsByOrderHashQuery } from '@/services/investigations'
import { investigationStatusValueToLabel } from '@/constants/statuses'
import type { InvestigationItem } from '@/components/shared/InvestigationsList'

interface UseInvestigationsCardsParams {
  patientHash?: string
  orderHash?: string
}

/**
 * Reusable hook that fetches investigations for a patient or order
 * and transforms them into the shape expected by InvestigationsList.
 */
export function useInvestigationsCards({ patientHash, orderHash }: UseInvestigationsCardsParams) {
  const patientQuery = useInvestigationsByPatientHashQuery(patientHash)
  const orderQuery = useInvestigationsByOrderHashQuery(orderHash)
  const { data: investigations, isLoading } = patientHash ? patientQuery : orderQuery

  const investigationItems: InvestigationItem[] = useMemo(() => {
    if (!investigations) return []

    return investigations.map(item => ({
      id: item.orderItemHash,
      name: item.orderItemName ?? item.productName ?? '',
      statusRaw: item.orderItemStatus ?? '',
      statusLabel: item.orderItemStatus ? investigationStatusValueToLabel(item.orderItemStatus) : '',
      createdAt: item.createdAt ?? '',
      updatedAt: item.updatedAt ?? '',
      type: item.productName ?? undefined
    }))
  }, [investigations])

  return { investigations: investigationItems, isLoading }
}
